import type { RefObject } from "react";

import { identify, tracker } from "./tracking";

export const trackPageLoaded = () => {
  tracker({
    event: "page_loaded",
  });
};

export const trackScroll = (props: { scroll: number }) => {
  tracker({
    event: "scroll",
    data: { scroll_percentage: props.scroll },
  });
};

export const videoStarted = (props: {
  label: string;
  videoLength: string;
  watchTime: string;
}) => {
  tracker({
    event: "video_action",
    data: {
      section: "top_banner",
      component_name: "video",
      label: props.label,
      entity_action: "play",
      video_length: props.videoLength,
      video_watch_time: props.watchTime,
    },
  });
};

export const videoAutoPlay = (props: {
  label: string;
  videoLength: string;
  watchTime: number;
}) => {
  tracker({
    event: "video_action",
    data: {
      section: "top_banner",
      component_name: "video",
      label: props.label,
      entity_action: "auto_play",
      video_length: props.videoLength,
      video_watch_time: props.watchTime,
    },
  });
};

export const videoPause = (props: {
  label: string;
  videoLength: string;
  watchTime: number;
}) => {
  tracker({
    event: "video_action",
    data: {
      section: "top_banner",
      component_name: "video",
      label: props.label,
      entity_action: "paused",
      video_length: props.videoLength,
      video_watch_time: props.watchTime,
    },
  });
};

export const videoProgressChanged = (props: {
  label: string;
  videoLength: string;
  progress: number;
  watchTime: number;
}) => {
  tracker({
    event: "video_action",
    data: {
      section: "top_banner",
      component_name: "video",
      label: props.label,
      entity_value: props.progress,
      entity_action: "progress_changed",
      video_length: props.videoLength,
      video_watch_time: props.watchTime,
    },
  });
};

export const videoStopped = (props: {
  label: string;
  videoLength: string;
  progress: number;
  watchTime: number;
}) => {
  tracker({
    event: "video_action",
    data: {
      section: "top_banner",
      component_name: "video",
      label: props.label,
      entity_value: props.progress,
      entity_action: "stopped",
      video_length: props.videoLength,
      video_watch_time: props.watchTime,
    },
  });
};

export const headerNavigationClick = (props: {
  section: string;
  component: string;
  label: string;
  parent?: string;
  redirectLink: string;
  pageKey?: string;
}) => {
  tracker({
    event: "navigation_click",
    data: {
      section: props.section,
      component_name: props.component,
      label: props.label,
      parent_entity: props.parent,
      landing_url: props.redirectLink,
      landing_page_key: props.pageKey,
    },
  });
};

export const headerCTAClick = (props: {
  section: string;
  component: string;
  label: string;
  parent?: string;
  entity: string;
}) => {
  tracker({
    event: "cta_click",
    data: {
      section: props.section,
      component_name: props.component,
      label: props.label,
      parent_entity: props?.parent || "",
      entity_name: props.entity,
    },
  });
};

export const headerExpansionClick = (props: {
  section: string;
  component: string;
  label: string;
  entity_name: string;
}) => {
  tracker({
    event: "expansion_click",
    data: {
      section: props.section,
      component_name: props.component,
      label: props.label,
      entity_name: props.entity_name,
    },
  });
};

export const cardClick = (props: {
  section: string;
  component: string;
  label: string;
  parent: string;
  redirectLink: string;
  pageKey: string;
}) => {
  tracker({
    event: "card_click",
    data: {
      section: props.section,
      component_name: props.component,
      label: props.label,
      parent_entity: props.parent,
      landing_url: props.redirectLink,
      landing_page_key: props.pageKey,
    },
  });
};
export const FooterNavigation = (props: {
  entityName: string;
  label: string;
  parent: string;
  redirectLink: string;
  pageKey?: string;
}) => {
  tracker({
    event: "navigation_click",
    data: {
      section: "footer",
      component_name: "footer_elements",
      entity_name: props.entityName,
      label: props.label,
      parent_entity: props.parent,
      landing_url: props.redirectLink,
      landing_page_key: props.pageKey,
    },
  });
};

export const iconClick = (props: {
  section: string;
  component: string;
  entity: string;
  landing_url: string;
  landing_page_key: string;
}) => {
  tracker({
    event: "icon_click",
    data: {
      section: props.section,
      component_name: props.component,
      label: props.entity,
      entity_name: props.entity,
      landing_url: props.landing_url,
      landing_page_key: props.landing_page_key,
    },
  });
};

export const faqSection = (props: {
  component: string;
  label: string;
  parent: string;
  entity: string;
  entityAction?: boolean;
}) => {
  tracker({
    event: "expansion_click",
    data: {
      section: "faq",
      component_name: props.component,
      label: props.label,
      parent_entity: props.parent,
      entity: props.entity,
      entityAction: props.entityAction,
    },
  });
};

export const tabSwitched = (props: {
  section: string;
  component: string;
  label: string;
}) => {
  tracker({
    event: "tab_switched",
    data: {
      section: props.section,
      component_name: props.component.toLowerCase(),
      label: props.label,
    },
  });
};

export const expansionClick = (props: {
  section: string;
  component: string;
  label: string;
  index?: string;
  entity_name?: string;
  parent_entity?: string;
}) => {
  tracker({
    event: "expansion_click",
    data: {
      section: props.section,
      component_name: props.component,
      label: props.label,
      position: props?.index || "",
      entity_name: props?.entity_name || "",
      parent_entity: props?.parent_entity || "",
    },
  });
};

export const navigationClick = (props: {
  section: string;
  component: string;
  label: string;
  index?: string;
  redirectLink?: string;
  parent?: string;
}) => {
  tracker({
    event: "navigation_click",
    data: {
      section: props.section,
      component_name: props.component,
      label: props.label,
      position: props?.index || "",
      landing_url: props?.redirectLink || "",
      parent_entity: props?.parent || "",
    },
  });
};

export const syllabusViewMore = (props: { label: string; index: string }) => {
  tracker({
    event: "cta_click",
    data: {
      section: "syllabus",
      component_name: "view_more",
      label: props.label,
      position: props.index,
    },
  });
};

export const programCompare = (props: {
  landingUrl: string;
  index: number;
}) => {
  tracker({
    event: "cta_click",
    data: {
      section: "programs",
      component_name: "compare",
      label: "compare",
      landing_url: props.landingUrl,
      position: props.index,
    },
  });
};

export const leadCtaClick = (props: {
  section: string;
  component: string;
  label: string;
  index?: number;
  parentEntity?: string;
}) => {
  tracker({
    event: "lead_cta_click",
    data: {
      section: props.section,
      component_name: props.component,
      label: props.label,
      position: props.index,
      parentEntity: props.parentEntity || "",
    },
  });
};

export const ctaClick = (props: {
  section: string;
  component: string;
  label: string;
  step: string;
  entity: string;
  landing_url?: string;
  landing_page_key?: string;
  parentEntity?: string;
}) => {
  tracker({
    event: "cta_click",
    data: {
      section: props.section,
      component_name: props.component,
      label: props.label,
      medium: "mobile_number_flow",
      seq_step: props.step,
      entity_name: props.entity,
      landing_url: props.landing_url,
      landing_page_key: props.landing_page_key,
      parentEntity: props.parentEntity || "",
    },
  });
};

export const modalView = (props: {
  section: string;
  component: string;
  step: string;
  label: string;
}) => {
  tracker({
    event: "modal_view",
    data: {
      section: props.section,
      component_name: props.component,
      label: props.label,
      medium: "mobile_number_flow",
      seq_step: props.step,
      entity_name: "lead_submit_modal",
    },
  });
};

export const minimalEdit = (props: {
  section: string;
  component: string;
  step: string;
  entity: string;
  label: string;
}) => {
  tracker({
    event: "minimal_edit",
    data: {
      section: props.section,
      component_name: props.component,
      label: props.label,
      medium: "mobile_number_flow",
      seq_step: props.step,
      entity_name: props.entity,
    },
  });
};

export const authContinue = (props: {
  section: string;
  component: string;
  step: string;
  label: string;
}) => {
  tracker({
    event: "auth_continue_click",
    data: {
      section: props.section,
      component_name: props.component,
      label: props.label,
      medium: "mobile_number_flow",
      seq_step: props.step,
    },
  });
};

export const authContinueEnabled = (props: {
  section: string;
  component: string;
  step: string;
  label: string;
}) => {
  tracker({
    event: "continue_button_active_transformation",
    data: {
      section: props.section,
      component_name: props.component,
      label: props.label,
      medium: "mobile_number_flow",
      seq_step: props.step,
    },
  });
};

export const authSuccess = (props: {
  section: string;
  component: string;
  additionalInfo?: Record<string, unknown>;
  // TODO: Fix this type
  userInfo: {
    id?: 1238;
    area_of_interest?: string;
    university?: string;
    course_id?: null;
    created_at?: null;
    email: string;
    highest_qualification?: string;
    leads_id?: null;
    message?: null;
    mobile?: string;
    name?: string;
    source_campaign?: null;
    source_media?: null;
    source_params?: null;
    source_path?: null;
    source_refer_url?: null;
    source_source?: null;
    specialization_id?: null;
    lead_identifier?: null;
    status?: null;
    uni_code?: null;
  };
  label: string;
  isFirstByUser: boolean;
}) => {
  tracker({
    event: "auth_success",
    data: {
      section: props.section,
      component_name: props.component,
      label: props.label,
      medium: "mobile_number_flow",
      additional_info: JSON.stringify(props.additionalInfo),
      first_by_user: props.isFirstByUser,
      first_by_user_and_program: props.isFirstByUser,
    },
  });
  identify({
    userId: props.userInfo.email,
    traits: {
      name: props.userInfo.name,
      email: props.userInfo.email,
    },
  });
};

export const authError = (props: {
  section: string;
  component: string;
  label: string;
  step: string;
  errorType: number | string;
  errorMessage: string;
  entity?: string;
}) => {
  tracker({
    event: "error_message",
    data: {
      section: props.section,
      component_name: props.component,
      label: props.label,
      medium: "mobile_number_flow",
      seq_step: props.step,
      error_type: props.errorType,
      error_message: props.errorMessage,
      entity_name: props.entity,
    },
  });
};

export const leadInfoCaptured = (props: {
  section: string;
  component: string;
  label: string;
  capturedInfo: Record<string, unknown>;
}) =>
  tracker({
    event: "lead_info_captured",
    data: {
      section: props.section,
      component_name: props.component,
      label: props.label,
      medium: "mobile_number_flow",
      additional_info: JSON.stringify(props.capturedInfo),
    },
  });

export const leadSubmitted = (props: {
  section: string;
  component: string;
  label: string;
  capturedInfo: Record<string, unknown>;
  isFirstByUser: boolean;
  parentEntity: string;
}) => {
  tracker({
    event: "lead_submitted",
    data: {
      section: props.section,
      component_name: props.component,
      label: props.label,
      medium: "mobile_number_flow",
      additional_info: JSON.stringify(props.capturedInfo),
      first_by_user: props.isFirstByUser,
      first_by_user_and_program: props.isFirstByUser,
      parent_entity: props.parentEntity,
    },
  });
};

export const modalClosedLeadDrop = (props: {
  section: string;
  component: string;
  step: string;
  label: string;
}) => {
  tracker({
    event: "modal_closed_clicked",
    data: {
      section: props.section,
      component_name: props.component,
      label: props.label,
      medium: "mobile_number_flow",
      seq_step: props.step,
      entity_name: "lead_submit_modal",
    },
  });
};

export const blogOrProgramCard = (props: {
  section: string;
  component: string;
  label: string;
  position: number;
  url: string;
}) => {
  tracker({
    event: "card_click",
    data: {
      section: props.section,
      component_name: props.component,
      label: props.label,
      position: props.position,
      landing_url: props.url,
    },
  });
};

export const benefitCtaClicked = (props: { label: string }) => {
  tracker({
    event: "cta_click",
    data: {
      section: "online_degree_advantages",
      component_name: "online_degree_highlight_card",
      label: props.label,
    },
  });
};

export const filterAction = (props: { label: string }) => {
  tracker({
    event: "filter_action",
    data: {
      section: "filters",
      component_name: "filter_by_need",
      label: props.label,
    },
  });
};

export const filterCtaClick = (props: { label: string; url: string }) => {
  tracker({
    event: "cta_click",
    data: {
      section: "filters",
      component_name: "filter_by_need",
      label: props.label,
      landing_url: props.url,
    },
  });
};

export const chatNow = () => {
  tracker({
    event: "lead_cta_click",
    data: {
      section: "sticky_support",
      component_name: "chat_with_us",
      label: "whatsApp",
      medium: "whatsapp",
    },
  });
};

export const logoutClick = () => {
  tracker({
    event: "cta_click",
    data: {
      section: "top_navigation",
      component_name: "logout",
      label: "logout",
    },
  });
};

declare global {
  interface Window {
    currentSection: string;
  }
}

export const FireWidgetViewedEvent = (
  ref: RefObject<HTMLElement | null>,
  sectionName: string
) => {
  if (ref.current !== null) {
    const observer = new IntersectionObserver(
      ([element]) => {
        if (
          element?.isIntersecting &&
          (!window?.currentSection ||
            window?.currentSection === ref.current!.id)
        ) {
          tracker({
            event: "widget_viewed",
            data: {
              section: sectionName,
              label: "",
            },
          });
          if (window != null) window.currentSection = "";
          observer.unobserve(ref.current!);
        }
      },
      {
        rootMargin: "-50px",
        threshold: 0,
      }
    );

    observer.observe(ref.current);
  }
};

export const popupViewed = (props: { section: string; label: string }) => {
  tracker({
    event: "popup_viewed",
    data: {
      section: props.section,
      label: props.label,
    },
  });
};

export const modalClosed = (props: { section: string; label: string }) => {
  tracker({
    event: "modal_closed_clicked",
    data: {
      section: props.section,
      label: props.label,
    },
  });
};

export const carouselClick = (props: { section: string; label: string }) => {
  tracker({
    event: "carousel_click",
    data: props,
  });
};

export const videoAction = (props: { section: string; label: string }) => {
  tracker({
    event: "video_action",
    data: props,
  });
};

export const lifeAfterCUScroll = () => {
  tracker({
    event: "scroll",
    data: {
      section: "life_after_cu",
    },
  });
};

export const VideoModalClosed = (props: { label: string }) => {
  tracker({
    event: "modal_closed_clicked",
    data: {
      section: "walk_through",
      component_name: "video",
      label: props.label,
    },
  });
};

export const VideoButtonClick = (props: { label: string }) => {
  tracker({
    event: "video_action",
    data: {
      section: "walk_through",
      component_name: "video",
      label: props.label,
      entity_action: "button_clicked",
      video_watch_time: 0,
    },
  });
};

export const buttonHover = (props: {
  section: string;
  component?: string;
  label: string;
}) => {
  tracker({
    event: "button_hover",
    data: {
      section: props.section,
      component_name: props.component || "",
      label: props.label,
    },
  });
};
